import { Fragment, useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { BackLink } from "@/components/BackLink";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, CheckCircle, XCircle, FileText, Save, CheckSquare, Square, AlertTriangle, DollarSign, MapPin, Navigation, UserPlus, Loader2, Search, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const _shortTerm = (name) => {
  const m = /term\s*(\d+)/i.exec(String(name || ""));
  return m ? `T${m[1]}` : String(name || "this term");
};

export default function AcademyAttendance({ user }) {
  const { sessionId, playerId } = useParams();
  const [session, setSession] = useState(null);
  const [players, setPlayers] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [paymentStatuses, setPaymentStatuses] = useState({});
  const [showPaymentStatus, setShowPaymentStatus] = useState(false);
  // "Can they make it?" answers from parents (portal or WhatsApp reply)
  const [rsvps, setRsvps] = useState({});
  const [prefilled, setPrefilled] = useState(0);
  // Who marked each player, and whether an admin has signed the session off.
  const [markedBy, setMarkedBy] = useState({});
  const [confirmed, setConfirmed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  
  // Coach check-in state
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkinStatus, setCheckinStatus] = useState(null);
  const [clash, setClash] = useState(null);
  const [workType, setWorkType] = useState("coaching");
  const [locationError, setLocationError] = useState(null);

  // Guest player form state
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [trialists, setTrialists] = useState([]);
  const [showTrialForm, setShowTrialForm] = useState(false);
  const [showTrialPick, setShowTrialPick] = useState(false);
  const [weekTrials, setWeekTrials] = useState([]);
  const [addingTrial, setAddingTrial] = useState(false);
  const [trialForm, setTrialForm] = useState({ child_name: "", parent_name: "", parent_contact: "" });
  const [guestForm, setGuestForm] = useState({ name: "", parent_name: "", parent_contact: "" });
  const [guestPhoto, setGuestPhoto] = useState(null);
  const [addingGuest, setAddingGuest] = useState(false);

  // Drop-in roster search state (C5 — May 2026)
  // Lets coaches add a registered academy player who shows up to a session
  // they weren't booked into, instead of creating a duplicate guest record.
  const [showDropInSearch, setShowDropInSearch] = useState(false);
  const [showNotSignedUp, setShowNotSignedUp] = useState(false);
  const [dropInQuery, setDropInQuery] = useState("");
  const [dropInResults, setDropInResults] = useState([]);
  const [dropInSearching, setDropInSearching] = useState(false);
  const [addingDropIn, setAddingDropIn] = useState(null); // player_id being added

  const isCoach = user?.roles?.includes("academy_coach");
  const isParent = user?.roles?.includes("academy_parent");
  const isAdmin = ["convenor", "captain", "academy_admin"].includes(user?.role);

  useEffect(() => {
    if (sessionId) {
      fetchSessionAndPlayers();
      fetchCheckinStatus();
      loadTrialists();
    } else if (playerId) {
      fetchPlayerAttendance();
    }
  }, [sessionId, playerId]);

  // Fetch coach check-in status
  const fetchCheckinStatus = async () => {
    if (!sessionId || !isCoach) return;
    
    try {
      const response = await axios.get(
        `${API}/academy/coach/checkin-status/${sessionId}`,
        { withCredentials: true }
      );
      setCheckinStatus(response.data);
    } catch (error) {
      console.log("Check-in status not available:", error);
    }
  };

  // No signal, or GPS refused: still check in, flagged for the admin to approve.
  // The same qualifying question applies — a clash is about the time, not the GPS.
  const handleCheckinWithoutGps = async (reason, chosenType = null, skipClashCheck = false) => {
    if (!skipClashCheck && await checkForClash("nogps", reason)) return;
    setCheckingIn(true);
    try {
      const response = await axios.post(
        `${API}/academy/coach/checkin/${sessionId}`,
        { reason: reason || "No GPS available", work_type: chosenType || workType },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setCheckinStatus({ user_checked_in: true, user_checkin: response.data.checkin });
      setLocationError(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check in");
    } finally {
      setCheckingIn(false);
    }
  };

  // Already checked in elsewhere at this time? Ask before paying him twice.
  const checkForClash = async (via = "gps", reason = "") => {
    try {
      const r = await axios.get(`${API}/academy/coach/checkin-clash/${sessionId}`,
        { withCredentials: true });
      if (r.data.clash) {
        setClash({ ...r.data, via, reason });
        return true;
      }
    } catch (e) {
      // A clash check that fails must never block a check-in.
    }
    return false;
  };

  // Coach check-in with GPS
  // chosenType is passed explicitly: setWorkType() hasn't landed yet when the
  // clash answer calls straight back in here.
  const handleCoachCheckin = async (skipClashCheck = false, chosenType = workType) => {
    if (!skipClashCheck && await checkForClash()) return;
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }
    
    setCheckingIn(true);
    setLocationError(null);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await axios.post(
            `${API}/academy/coach/checkin/${sessionId}`,
            {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              work_type: chosenType
            },
            { withCredentials: true }
          );
          
          toast.success(response.data.message);
          setCheckinStatus({
            user_checked_in: true,
            user_checkin: response.data.checkin
          });
          
          if (!response.data.location_verified && response.data.distance_meters) {
            toast.warning(`Location mismatch: ${response.data.distance_meters}m from venue`, {
              duration: 5000
            });
          }
        } catch (error) {
          console.error("Check-in error:", error);
          toast.error(error.response?.data?.detail || "Failed to check in");
        } finally {
          setCheckingIn(false);
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        let errorMessage = "Failed to get location";
        if (error.code === 1) errorMessage = "Location access denied. Please allow location access.";
        if (error.code === 2) errorMessage = "Location unavailable. Please try again.";
        if (error.code === 3) errorMessage = "Location request timed out. Please try again.";
        
        setLocationError(errorMessage);
        toast.error(errorMessage);
        setCheckingIn(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  // Fetch payment statuses for players (for coaches/admins)
  const fetchPaymentStatuses = async (playerList, termId) => {
    if (!playerList.length || (!isCoach && !isAdmin)) return;
    
    try {
      const playerIds = playerList.map(p => p.id).join(',');
      const response = await axios.get(
        `${API}/academy/coach/player-payment-status?player_ids=${playerIds}${termId ? `&term_id=${termId}` : ''}`,
        { withCredentials: true }
      );
      
      if (response.data.players && response.data.players.length > 0) {
        const statusMap = {};
        response.data.players.forEach(p => {
          statusMap[p.player_id] = {
            status: p.payment_status,
            registered: p.registered,
            paid: p.paid,
            outstanding: p.outstanding,
            unpaid: p.sessions_unpaid,
            warning: p.show_warning,
            days: p.days,
            days_per_week: p.days_per_week,
            programme: p.programme,
            social: p.social,
            terms: p.terms || [],
            registeredThisTerm: p.registered_this_term,
          };
        });
        setPaymentStatuses(statusMap);
        // The payment column only appears when payment really was returned.
        setShowPaymentStatus(response.data.players.some(p => p.payment_status));
      }
    } catch (error) {
      // Payment visibility might be disabled - that's OK
      console.log("Payment status not available:", error.response?.data?.message);
    }
  };

  const fetchSessionAndPlayers = async () => {
    try {
      // Fetch the specific session by id (works even when there are >200 sessions)
      let foundSession = null;
      try {
        const sessionResponse = await axios.get(`${API}/academy/sessions/${sessionId}`, {
          withCredentials: true
        });
        foundSession = sessionResponse.data;
      } catch (err) {
        // Fallback to the bulk list in case the id-endpoint is unavailable
        const sessionsResponse = await axios.get(`${API}/academy/sessions`, {
          withCredentials: true
        });
        foundSession = sessionsResponse.data.find(s => s.id === sessionId);
      }
      setSession(foundSession);

      if (foundSession) {
        // Team lookup is best-effort — a session can exist without a team
        // (bucket-only groups), and that must never blank out the roster.
        let teamPlayers = [];
        if (foundSession.team_id) {
          try {
            const teamResponse = await axios.get(`${API}/academy/teams/${foundSession.team_id}`, {
              withCredentials: true
            });
            teamPlayers = teamResponse.data?.players || [];
          } catch (teamErr) {
            console.warn("Team lookup failed, falling back to the session roster");
          }
        }
        const signups = foundSession.signups || [];
        // C3 fix (May 2026): The wizard registration flow records enrolled
        // children in `enrolled_player_ids[]`, NOT `signups[]`. The legacy
        // `signups[]` array only carries self-signups + inline guest dicts.
        // Reading only signups meant coaches saw an empty roster for every
        // session populated by the wizard. Union both sources here.
        const enrolledIds = foundSession.enrolled_player_ids || [];

        // Oct 2026 — a holding team exists precisely for children who are NOT
        // enrolled in a session yet. Filtering the roster down to enrolled ids
        // left the U12 coach with an empty register. Everyone on the team is on
        // the register; their term status is shown as a badge instead.
        const signedUpPlayers = [...teamPlayers];

        // Fallback (Jun 2026): with immediate placement, a child can be on the
        // session's enrolled_player_ids before being mirrored onto team.players.
        // Pull any such enrolled players from the authoritative session-players
        // endpoint (same one the Roster page uses) so attendance never shows empty.
        const coveredIds = new Set(signedUpPlayers.map(p => p.id));
        const missingEnrolled = enrolledIds.filter(id => !coveredIds.has(id));
        if (missingEnrolled.length) {
          try {
            const spId = foundSession.session_template_id || sessionId;
            const sp = await axios.get(`${API}/academy/admin/session/${spId}/players`, { withCredentials: true });
            (sp.data || []).forEach(p => {
              if (missingEnrolled.includes(p.id) && !coveredIds.has(p.id)) {
                signedUpPlayers.push({ ...p, child_name: p.child_name || p.name });
                coveredIds.add(p.id);
              }
            });
          } catch (_) { /* endpoint optional */ }
        }

        // Guest players live inside `signups` as dicts with is_guest=true.
        // The legacy `guest_players` field is kept as a fallback for older data.
        const inlineGuests = signups
          .filter(s => typeof s === 'object' && s !== null && s.is_guest)
          .map(g => ({
            id: g.guest_id || `guest_${g.name}`,
            child_name: g.name,
            is_guest: true,
            email: g.email,
            parent_name: g.parent_name,
            parent_contact: g.parent_contact,
          }));
        const legacyGuests = (foundSession.guest_players || []).map(g => ({
          id: g.guest_id || `guest_${g.name}`,
          child_name: g.name,
          is_guest: true,
          email: g.email,
        }));
        const guestPlayers = [...inlineGuests, ...legacyGuests];
        
        const allPlayers = [...signedUpPlayers, ...guestPlayers];
        setPlayers(allPlayers);
        
        // Fetch payment statuses for coaches/admins
        await fetchPaymentStatuses(allPlayers, foundSession.term_id);

        // Attendance intentions for this date — parents answer in the portal or
        // by replying to the WhatsApp poll. Used to pre-fill the sheet.
        const rsvpMap = {};
        const dateIso = String(foundSession.session_date || "").slice(0, 10);
        if (dateIso) {
          try {
            const rr = await axios.get(`${API}/academy/session-rsvps`, {
              params: { date: dateIso }, withCredentials: true,
            });
            (rr.data?.rsvps || []).forEach((r) => { rsvpMap[r.player_id] = r; });
          } catch (_) { /* availability is optional */ }
        }
        setRsvps(rsvpMap);

        const buildInitial = () => {
          const initial = {};
          let pre = 0;
          allPlayers.forEach((player) => {
            const r = rsvpMap[player.id];
            if (r && typeof r.attending === "boolean") {
              initial[player.id] = r.attending ? "present" : "absent";
              pre += 1;
            } else {
              initial[player.id] = "";
            }
          });
          setPrefilled(pre);
          return initial;
        };

        // Always read back what's already saved — a session can have marks
        // even when the old attendance_taken flag was never set.
        try {
          const attendanceResponse = await axios.get(
            `${API}/academy/sessions/${sessionId}/attendance`,
            { withCredentials: true }
          );

          const records = attendanceResponse.data?.attendance || [];
          if (records.length) {
            const loadedAttendance = {};
            const marks = {};
            records.forEach(record => {
              loadedAttendance[record.player_id] = record.status;
              marks[record.player_id] = {
                by: record.marked_by_name,
                at: record.marked_at,
                confirmed: !!record.admin_confirmed,
              };
            });
            // Someone marked but no longer on the roster still belongs on the
            // register — otherwise the marks look lost.
            const known = new Set(allPlayers.map(p => p.id));
            const extras = records
              .filter(r => !known.has(r.player_id))
              .map(r => ({
                id: r.player_id,
                child_name: r.child_name || "Former squad member",
                _off_roster: true,
              }));
            if (extras.length) setPlayers([...allPlayers, ...extras]);
            setAttendance(loadedAttendance);
            setMarkedBy(marks);
            setConfirmed(!!attendanceResponse.data?.summary?.confirmed);
            setPrefilled(0);
            setViewMode(true);
          } else {
            setAttendance(buildInitial());
          }
        } catch (error) {
          console.error("Error loading existing attendance:", error);
          setAttendance(buildInitial());
        }
      }
    } catch (error) {
      console.error("Error fetching session:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to load session details";
      toast.error(errorMsg);
      console.error("Full error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayerAttendance = async () => {
    try {
      const response = await axios.get(`${API}/academy/attendance/${playerId}`, {
        withCredentials: true
      });
      setPlayers([]);
      setAttendance(response.data);
      setViewMode(true);
    } catch (error) {
      console.error("Error fetching player attendance:", error);
      toast.error("Failed to load attendance records");
    } finally {
      setLoading(false);
    }
  };

  const handleAttendanceChange = (playerId, status) => {
    setAttendance(prev => ({
      ...prev,
      [playerId]: status
    }));
  };

  const togglePlayerSelection = (playerId) => {
    setSelectedPlayers(prev => 
      prev.includes(playerId) 
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId]
    );
  };

  const selectAll = () => {
    // Everyone currently on screen — on a phone the coach ticks who turned up
    // and presses one button, so re-ticking a marked child must still work.
    const onScreen = visiblePlayers.length ? visiblePlayers : players;
    setSelectedPlayers(onScreen.map(p => p.id));
    if (!onScreen.length) toast.info("Nobody on this register yet");
  };

  const deselectAll = () => {
    setSelectedPlayers([]);
  };

  const markSelectedAs = (status) => {
    if (selectedPlayers.length === 0) {
      toast.error("Please select at least one player");
      return;
    }

    const newAttendance = { ...attendance };
    selectedPlayers.forEach(playerId => {
      newAttendance[playerId] = status;
    });
    setAttendance(newAttendance);
    toast.success(`Marked ${selectedPlayers.length} player(s) as ${status}`);
    setSelectedPlayers([]);
  };

  const handleAddGuest = async () => {
    const name = guestForm.name.trim();
    if (!name) {
      toast.error("Guest player name is required");
      return;
    }
    if (!guestForm.parent_name.trim() || !guestForm.parent_contact.trim()) {
      toast.error("Parent name and a phone or email are required for a walk-in");
      return;
    }
    setAddingGuest(true);
    try {
      const payload = {
        name,
        parent_name: guestForm.parent_name.trim(),
        parent_contact: guestForm.parent_contact.trim(),
      };
      await axios.post(
        `${API}/academy/sessions/${sessionId}/add-guest`,
        payload,
        { withCredentials: true }
      );
      // Phase 3: also flag as a walk-in for admin follow-up (with optional photo)
      try {
        const notesParts = [];
        if (payload.parent_name) notesParts.push(`Parent: ${payload.parent_name}`);
        if (payload.parent_contact) notesParts.push(`Contact: ${payload.parent_contact}`);
        const wi = await axios.post(
          `${API}/academy/sessions/${sessionId}/walk-in`,
          { player_name: name, notes: notesParts.join(" · ") },
          { withCredentials: true }
        );
        if (guestPhoto && wi.data?.id) {
          const fd = new FormData();
          fd.append("file", guestPhoto);
          await axios.post(`${API}/academy/walk-ins/${wi.data.id}/photo`, fd, {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      } catch (e) {
        console.warn("Walk-in follow-up record not created:", e);
      }
      toast.success(`Guest player "${name}" added${guestPhoto ? " with photo" : ""} — flagged for admin follow-up`);
      setGuestForm({ name: "", parent_name: "", parent_contact: "" });
      setGuestPhoto(null);
      setShowGuestForm(false);
      // Refresh player list so the guest appears and can be marked
      await fetchSessionAndPlayers();
    } catch (error) {
      console.error("Error adding guest:", error);
      toast.error(error.response?.data?.detail || "Failed to add guest player");
    } finally {
      setAddingGuest(false);
    }
  };

  const loadTrialists = async () => {
    try {
      const r = await axios.get(`${API}/academy/sessions/${sessionId}/trialists`,
        { withCredentials: true });
      setTrialists(r.data?.trialists || []);
    } catch {
      setTrialists([]);
    }
  };

  const loadWeekTrials = async () => {
    const day = (session?.session_date || session?.date || "").toString().slice(0, 10);
    if (!day) return;
    try {
      const r = await axios.get(`${API}/academy/trials/for-register`,
        { params: { date: day, days: 7 }, withCredentials: true });
      setWeekTrials(r.data?.rows || []);
    } catch {
      setWeekTrials([]);
    }
  };

  const markTrialist = async (trialId, status) => {
    try {
      const r = await axios.post(`${API}/academy/sessions/${sessionId}/trial-attendance`,
        { trial_id: trialId, status }, { withCredentials: true });
      toast.success(r.data?.message || "Trial recorded");
      setShowTrialPick(false);
      await loadTrialists();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record that trial");
    }
  };

  const addTrialist = async () => {
    setAddingTrial(true);
    try {
      const r = await axios.post(`${API}/academy/sessions/${sessionId}/add-trialist`, {
        child_name: trialForm.child_name.trim(),
        parent_name: trialForm.parent_name.trim(),
        parent_contact: trialForm.parent_contact.trim(),
      }, { withCredentials: true });
      toast.success(`${r.data.child_name} recorded as a trialist — not billed`);
      setTrialForm({ child_name: "", parent_name: "", parent_contact: "" });
      setShowTrialForm(false);
      await loadTrialists();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add that trialist");
    } finally {
      setAddingTrial(false);
    }
  };

  const confirmAttendance = async () => {
    setConfirming(true);
    try {
      await axios.post(`${API}/academy/sessions/${sessionId}/attendance/confirm`, {},
        { withCredentials: true });
      setConfirmed(true);
      toast.success("Register confirmed");
      fetchSessionAndPlayers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm the register");
    } finally {
      setConfirming(false);
    }
  };


  const saveAttendance = async () => {
    const marked = players.filter(p => attendance[p.id]);
    if (marked.length === 0) {
      toast.error("Mark at least one child before saving");
      return;
    }

    setSaving(true);
    try {
      // One card can cover several programmes sharing this slot. Each child's
      // mark is written against the session for the programme they are
      // registered for, so coach pay and billing stay on the right programme.
      const siblings = session?.sibling_sessions || [];
      const targetFor = (playerId) => {
        const hit = siblings.find((s) => (s.enrolled_player_ids || []).includes(playerId));
        if (hit) return hit.id;
        const termIds = (paymentStatuses[playerId]?.terms || []).map((t) => t.term_id);
        const byTerm = siblings.find((s) => termIds.includes(s.term_id));
        return byTerm ? byTerm.id : sessionId;
      };

      const batches = {};
      marked.forEach((player) => {
        const target = (session?.enrolled_player_ids || []).includes(player.id)
          ? sessionId
          : targetFor(player.id);
        (batches[target] = batches[target] || []).push({
          player_id: player.id,
          status: attendance[player.id],
        });
      });

      await Promise.all(Object.entries(batches).map(([sid, records]) =>
        axios.post(`${API}/academy/sessions/${sid}/attendance`, records,
          { withCredentials: true })));

      toast.success(`Attendance saved for ${marked.length} ${marked.length === 1 ? "child" : "children"}`);
      setViewMode(true);
      
      // Send MC notifications
      const mcPlayers = players.filter(p => attendance[p.id] === "mc");
      if (mcPlayers.length > 0) {
        toast.info(`MC notification will be sent to ${mcPlayers.length} parent(s)`);
      }
    } catch (error) {
      console.error("Error saving attendance:", error);
      toast.error(error.response?.data?.detail || "Failed to save attendance");
    } finally {
      setSaving(false);
    }
  };

  // Drop-in roster search (C5/C6 — May 2026) ---------------------------------
  const searchDropInRoster = async (q) => {
    if (!q || q.trim().length < 2) {
      setDropInResults([]);
      return;
    }
    setDropInSearching(true);
    try {
      const res = await axios.get(`${API}/academy/coach/search-players`, {
        params: { q: q.trim() },
        withCredentials: true,
      });
      // Hide players already in the session
      const alreadyInSession = new Set(players.map(p => p.id));
      setDropInResults((res.data || []).filter(p => !alreadyInSession.has(p.id)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Search failed");
      setDropInResults([]);
    } finally {
      setDropInSearching(false);
    }
  };

  // Debounced search trigger
  useEffect(() => {
    if (!showDropInSearch) return;
    const t = setTimeout(() => searchDropInRoster(dropInQuery), 300);
    return () => clearTimeout(t);
  }, [dropInQuery, showDropInSearch]);// eslint-disable-line

  const addDropInPlayer = async (player) => {
    setAddingDropIn(player.id);
    try {
      await axios.post(
        `${API}/academy/sessions/${sessionId}/add-enrolled-player`,
        { player_id: player.id },
        { withCredentials: true }
      );
      toast.success(`${player.child_name} added to this session`);
      // Append to local list and clear from search results
      setPlayers(prev => [...prev, { ...player, _drop_in: true }]);
      setAttendance(prev => ({ ...prev, [player.id]: "" }));
      setDropInResults(prev => prev.filter(p => p.id !== player.id));
      setDropInQuery("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add player");
    } finally {
      setAddingDropIn(null);
    }
  };

  // "Gabriel" is really Ng Chin Hong: point the guest at the child we already
  // have, move his mark across, and remember the nickname.
  const [linkingGuest, setLinkingGuest] = useState(null);

  const linkGuestToPlayer = async (player) => {
    if (!linkingGuest) return;
    try {
      const r = await axios.post(
        `${API}/academy/sessions/${sessionId}/link-guest`,
        { guest_id: linkingGuest.id, player_id: player.id, remember_alias: true },
        { withCredentials: true }
      );
      toast.success(
        `${linkingGuest.child_name} linked to ${r.data.child_name}`
        + (r.data.alias_added ? ` — also known as "${r.data.alias_added}"` : "")
      );
      setLinkingGuest(null);
      setDropInQuery("");
      setDropInResults([]);
      fetchSessionAndPlayers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not link that child");
    }
  };




  const enrolledForThisSlot = new Set([
    ...((session?.enrolled_player_ids) || []),
    ...((session?.sibling_sessions || []).flatMap((s) => s.enrolled_player_ids || [])),
  ]);
  const isSignedUp = (p) => p.is_guest || p._off_roster
    || enrolledForThisSlot.has(p.id) || paymentStatuses[p.id]?.registeredThisTerm === true;
  const signedUpList = players.filter(isSignedUp);
  const notSignedUpList = players.filter((p) => !isSignedUp(p));
  const visiblePlayers = showNotSignedUp ? [...signedUpList, ...notSignedUpList] : signedUpList;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (playerId) {
    return (
      <div className="min-h-screen bg-[#0a0f1a]">
        <header className="bg-slate-950 border-b border-white/5 shadow-lg">
          <div className="container mx-auto px-4 py-4">
            <BackLink to="/academy" label="Back"
              className="text-white hover:text-blue-200"
              testId="attendance-player-back" />
          </div>
        </header>
        <div className="container mx-auto px-4 py-12">
          <Card className="p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Attendance History</h2>
            <p className="text-slate-400">Player attendance view coming soon</p>
          </Card>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Card className="p-8">
          <p className="text-slate-400">Session not found</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a] pb-20 sm:pb-0">
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <BackLink to="/academy/sessions" label="Back"
            className="text-white hover:text-blue-200"
            testId="attendance-session-back" />
          <h1 className="text-2xl font-bold text-white">Mark Attendance</h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Session Info */}
        <Card className="p-6 mb-6 bg-slate-900/50 border-2 border-blue-500/30">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-slate-400">Team</p>
              <p className="text-lg font-bold text-white">{session.team_name}</p>
              {session.term_name && (
                <p className="text-xs text-yellow-300/80 mt-0.5" data-testid="attendance-programme">
                  {session.term_name}
                </p>
              )}
            </div>
            <div>
              <p className="text-sm text-slate-400">Date</p>
              <p className="text-lg font-bold text-white">
                {new Date(session.session_date).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-400">Type</p>
              <p className="text-lg font-bold text-white capitalize">{session.session_type}</p>
            </div>
          </div>
          {players.length > 0 && (
            <p className="text-xs text-slate-400 mt-3" data-testid="attendance-roster-note">
              Everyone in this age group is listed, whether or not they have signed up for the term —
              the badge by each name shows which terms they are registered for and anything owing.
              Mark only the children who turned up.
            </p>
          )}
        </Card>

        {/* Coach Check-in Card - Only show to coaches */}
        {isCoach && (
          <Card className={`p-4 mb-6 border-2 ${
            checkinStatus?.user_checked_in 
              ? 'bg-green-500/10 border-green-500/50' 
              : 'bg-amber-500/10 border-amber-500/50'
          }`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-full ${
                  checkinStatus?.user_checked_in ? 'bg-green-500/20' : 'bg-amber-500/20'
                }`}>
                  {checkinStatus?.user_checked_in ? (
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  ) : (
                    <MapPin className="w-6 h-6 text-amber-400" />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-white">
                    {checkinStatus?.user_checked_in ? 'Checked In' : 'Coach Check-in'}
                  </h3>
                  {checkinStatus?.user_checked_in ? (
                    <p className="text-sm text-slate-400">
                      {new Date(checkinStatus.user_checkin?.checkin_time).toLocaleTimeString()}
                      {checkinStatus.user_checkin?.location_verified && (
                        <span className="ml-2 text-green-400">✓ Location verified</span>
                      )}
                      {checkinStatus.user_checkin?.distance_from_venue_meters && (
                        <span className="ml-2 text-slate-500">
                          ({checkinStatus.user_checkin.distance_from_venue_meters}m from venue)
                        </span>
                      )}
                    </p>
                  ) : (
                    <p className="text-sm text-slate-400">
                      Tap to verify your arrival at {session.location || 'training venue'}
                    </p>
                  )}
                </div>
              </div>
              
              {!checkinStatus?.user_checked_in && (
                <Button
                  onClick={() => handleCoachCheckin()}
                  disabled={checkingIn}
                  className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
                  data-testid="coach-checkin-btn"
                >
                  {checkingIn ? (
                    <>
                      <Navigation className="w-4 h-4 mr-2 animate-pulse" />
                      Getting location...
                    </>
                  ) : (
                    <>
                      <MapPin className="w-4 h-4 mr-2" />
                      I've Arrived
                    </>
                  )}
                </Button>
              )}
            </div>
            
            {locationError && (
              <div className="mt-3 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
                <p className="text-sm text-red-400">{locationError}</p>
                <p className="text-xs text-slate-400 mt-1">
                  Make sure location services are enabled in your browser/device settings.
                </p>
              </div>
            )}

            {clash && (
              <div className="mt-3 p-4 bg-amber-500/10 border border-amber-500/40 rounded-lg"
                data-testid="clash-question">
                <p className="text-sm text-white font-semibold">
                  You're already checked in at {clash.start_time} for{" "}
                  {clash.sessions.map((s) => s.team).join(", ")}.
                </p>
                <p className="text-xs text-slate-400 mb-3">
                  Did you coach this group too, or only mark their register?
                  This decides what you're paid.
                </p>
                <div className="space-y-2">
                  {[
                    ["combined", "I coached this group as well — merged into one session",
                      "both teams on the pitch together: paid once for the slot, not twice"],
                    ["register_only", "I didn't coach this group — I only marked their register",
                      "someone else ran it: no extra pay for this one, your own session is unaffected"],
                  ].map(([value, label, hint]) => (
                    <button key={value}
                      onClick={() => {
                        const via = clash.via;
                        const reason = clash.reason;
                        setWorkType(value);
                        setClash(null);
                        if (via === "nogps") {
                          handleCheckinWithoutGps(reason, value, true);
                        } else {
                          handleCoachCheckin(true, value);
                        }
                      }}
                      disabled={checkingIn}
                      className="w-full text-left px-3 py-2 rounded bg-slate-800/70 hover:bg-slate-700 transition-colors"
                      data-testid={`work-type-${value}`}>
                      <span className="text-sm text-slate-100 block">{label}</span>
                      <span className="text-xs text-slate-500">{hint}</span>
                    </button>
                  ))}
                </div>
                <button onClick={() => setClash(null)}
                  className="mt-2 text-xs text-slate-400 underline"
                  data-testid="clash-cancel">Cancel</button>
              </div>
            )}


            {!checkinStatus?.user_checked_in && (
              <button
                onClick={() => handleCheckinWithoutGps(locationError || "No GPS available")}
                disabled={checkingIn}
                className="mt-3 text-xs text-slate-400 underline hover:text-slate-200"
                data-testid="checkin-no-gps-btn"
              >
                No signal? Check in without GPS — the admin will approve it
              </button>
            )}

            {checkinStatus?.user_checkin?.approval_status === "pending" && (
              <p className="mt-3 text-xs text-amber-300" data-testid="checkin-pending-note">
                This check-in is waiting for admin approval. You can still mark the register.
              </p>
            )}
          </Card>
        )}


        {/* Drop-in Player Search (C5 — May 2026) — for registered academy
            kids who turn up without a booking. Avoids creating a duplicate
            guest record for a child already in the system. */}
        {!viewMode && (isCoach || isAdmin) && (
          <Card className="p-4 mb-6 bg-slate-900/50 border-2 border-blue-500/30" data-testid="dropin-search-card">
            {!showDropInSearch ? (
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-blue-500/20">
                    <Search className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">Drop-in Player?</h3>
                    <p className="text-sm text-slate-400">A registered kid showed up but isn't on this session's list — search and add them.</p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowDropInSearch(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="open-dropin-search-btn"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search Roster
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-white flex items-center gap-2">
                    <Search className="w-5 h-5 text-blue-400" />
                    {linkingGuest
                      ? `Who is "${linkingGuest.child_name}"?`
                      : "Search Academy Roster"}
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setShowDropInSearch(false); setDropInQuery(""); setDropInResults([]); setLinkingGuest(null); }}
                    className="text-slate-400 hover:text-white"
                    data-testid="close-dropin-search-btn"
                  >
                    Cancel
                  </Button>
                </div>
                <Input
                  autoFocus
                  type="text"
                  placeholder="Type child's name or membership number…"
                  value={dropInQuery}
                  onChange={(e) => setDropInQuery(e.target.value)}
                  className="bg-slate-800 border-slate-600 text-white"
                  data-testid="dropin-search-input"
                />
                {dropInSearching && (
                  <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="dropin-search-loading">
                    <Loader2 className="w-4 h-4 animate-spin" /> Searching…
                  </div>
                )}
                {!dropInSearching && dropInQuery.length >= 2 && dropInResults.length === 0 && (
                  <p className="text-sm text-slate-500" data-testid="dropin-no-results">
                    No matches in your team(s). Use "Add Guest Player" below if the child isn't yet registered.
                  </p>
                )}
                {dropInResults.length > 0 && (
                  <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="dropin-results-list">
                    {dropInResults.map((p) => (
                      <div
                        key={p.id}
                        className="flex items-center justify-between gap-3 p-3 bg-slate-800/60 rounded-lg border border-slate-700"
                        data-testid={`dropin-result-${p.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white">{p.child_name}</p>
                          <p className="text-xs text-slate-400">
                            {p.membership_number} • {p.age_group || "—"} • Team: {p.team_id || "Unassigned"}
                          </p>
                          {!!(p.also_known_as || []).length && (
                            <p className="text-xs text-slate-500">
                              also known as {p.also_known_as.join(", ")}
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          onClick={() => (linkingGuest ? linkGuestToPlayer(p) : addDropInPlayer(p))}
                          disabled={addingDropIn === p.id}
                          className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0"
                          data-testid={`add-dropin-${p.id}-btn`}
                        >
                          {addingDropIn === p.id
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : linkingGuest ? "This is him" : "Add"}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        )}


        {/* Trialists — their own block: never billed, never on the roster */}
        {(isCoach || isAdmin) && (
          <Card className="p-4 mb-6 bg-slate-900/50 border-2 border-emerald-500/30"
            data-testid="trialists-card">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
              <div>
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-400" />
                  Trialists {trialists.length > 0 && `(${trialists.length})`}
                </h3>
                <p className="text-xs text-slate-400">
                  First trial is free and is never billed. Marking them present closes the trial off.
                </p>
              </div>
              {!viewMode && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline"
                    onClick={() => { setShowTrialPick(!showTrialPick); if (!weekTrials.length) loadWeekTrials(); }}
                    data-testid="pick-trialist-btn">
                    Not on this list?
                  </Button>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={() => setShowTrialForm(!showTrialForm)}
                    data-testid="add-trialist-btn">
                    <UserPlus className="w-4 h-4 mr-1" /> Add a trialist
                  </Button>
                </div>
              )}
            </div>

            {trialists.length === 0 && !showTrialForm && !showTrialPick && (
              <p className="text-sm text-slate-500" data-testid="no-trialists">
                Nobody is booked in to trial at this session.
              </p>
            )}

            <div className="space-y-2">
              {trialists.map((t) => (
                <div key={t.trial_id}
                  className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-white/5 last:border-0"
                  data-testid={`trialist-${t.trial_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm text-slate-100">
                      {t.child_name}
                      <span className="text-xs text-emerald-300 ml-2">
                        Trial{t.trial_number > 1 ? ` #${t.trial_number}` : ""}
                      </span>
                      {t.chargeable && (
                        <span className="text-xs text-amber-300 ml-2">· chargeable</span>
                      )}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {[t.age_group, t.parent_name, t.parent_phone || t.parent_email]
                        .filter(Boolean).join(" · ")}
                      {!t.chargeable && " · not billed"}
                    </p>
                  </div>
                  {viewMode ? (
                    <span className="text-xs text-slate-400">
                      {t.status === "present" ? "Present" : t.status ? "No show" : "Not marked"}
                    </span>
                  ) : (
                    <div className="flex gap-1">
                      <Button size="sm"
                        variant={t.status === "present" ? "default" : "outline"}
                        className={t.status === "present" ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                        onClick={() => markTrialist(t.trial_id, "present")}
                        data-testid={`trial-present-${t.trial_id}`}>
                        Present
                      </Button>
                      <Button size="sm"
                        variant={t.status && t.status !== "present" ? "default" : "outline"}
                        className={t.status && t.status !== "present" ? "bg-slate-600" : ""}
                        onClick={() => markTrialist(t.trial_id, "no_show")}
                        data-testid={`trial-noshow-${t.trial_id}`}>
                        No show
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {showTrialPick && (
              <div className="mt-3 border-t border-white/5 pt-3" data-testid="trial-pick-list">
                <p className="text-xs text-slate-400 mb-2">
                  Trialists booked this week — tap to put them on this register:
                </p>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {weekTrials.filter((w) => !trialists.some((t) => t.trial_id === w.trial_id))
                    .map((w) => (
                      <button key={w.trial_id}
                        onClick={() => markTrialist(w.trial_id, "present")}
                        className="w-full text-left px-3 py-2 rounded bg-slate-800/60 hover:bg-slate-700 text-sm text-slate-200"
                        data-testid={`trial-pick-${w.trial_id}`}>
                        {w.child_name}
                        <span className="text-xs text-slate-500 ml-2">
                          {[w.trial_date, w.age_group, w.parent_name].filter(Boolean).join(" · ")}
                        </span>
                      </button>
                    ))}
                  {!weekTrials.length && (
                    <p className="text-xs text-slate-500">No trials booked this week.</p>
                  )}
                </div>
              </div>
            )}

            {showTrialForm && (
              <div className="mt-3 border-t border-white/5 pt-3 space-y-3" data-testid="trial-form">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Child&apos;s name *</label>
                    <Input value={trialForm.child_name}
                      onChange={(e) => setTrialForm({ ...trialForm, child_name: e.target.value })}
                      placeholder="Child's full name"
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="trial-child-input" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Parent name *</label>
                    <Input value={trialForm.parent_name}
                      onChange={(e) => setTrialForm({ ...trialForm, parent_name: e.target.value })}
                      placeholder="Parent / guardian name"
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="trial-parent-input" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Parent contact *</label>
                    <Input value={trialForm.parent_contact}
                      onChange={(e) => setTrialForm({ ...trialForm, parent_contact: e.target.value })}
                      placeholder="Phone or email"
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="trial-contact-input" />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pb-12 sm:pb-0">
                  <Button variant="ghost" size="sm" onClick={() => setShowTrialForm(false)}
                    className="text-slate-400">Cancel</Button>
                  <Button size="sm" onClick={addTrialist}
                    disabled={addingTrial || !trialForm.child_name.trim()
                      || !trialForm.parent_name.trim() || !trialForm.parent_contact.trim()}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="submit-trialist-btn">
                    {addingTrial ? "Adding..." : "Add & mark present"}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Add Guest Player - Coach or Admin, only when not in view mode */}
        {!viewMode && (isCoach || isAdmin) && (
          <Card className="p-4 mb-6 bg-slate-900/50 border-2 border-purple-500/30" data-testid="guest-player-card">
            {!showGuestForm ? (
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-purple-500/20">
                    <UserPlus className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white">Unregistered / Guest Player Showed Up?</h3>
                    <p className="text-sm text-slate-400">Capture their name & parent contact so the admin can follow up.</p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowGuestForm(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="add-guest-btn"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Guest Player
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-white flex items-center gap-2">
                    <UserPlus className="w-5 h-5 text-purple-400" />
                    New Guest Player
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowGuestForm(false);
                      setGuestForm({ name: "", parent_name: "", parent_contact: "" });
                      setGuestPhoto(null);
                    }}
                    className="text-slate-400 hover:text-white"
                  >
                    Cancel
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Player Name *</label>
                    <Input
                      value={guestForm.name}
                      onChange={(e) => setGuestForm({ ...guestForm, name: e.target.value })}
                      placeholder="Child's full name"
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="guest-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Parent Name *</label>
                    <Input
                      value={guestForm.parent_name}
                      onChange={(e) => setGuestForm({ ...guestForm, parent_name: e.target.value })}
                      placeholder="Parent / guardian name"
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="guest-parent-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-400 mb-1 block">Parent Contact *</label>
                    <Input
                      value={guestForm.parent_contact}
                      onChange={(e) => setGuestForm({ ...guestForm, parent_contact: e.target.value })}
                      placeholder="Phone or email"
                      className="bg-slate-800 border-slate-700 text-white"
                      data-testid="guest-parent-contact-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-400 mb-1 block">Photo (optional — evidence for follow-up)</label>
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => setGuestPhoto(e.target.files?.[0] || null)}
                    className="block w-full text-sm text-slate-300 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-purple-600 file:text-white hover:file:bg-purple-700"
                    data-testid="guest-photo-input"
                  />
                  {guestPhoto && <p className="text-xs text-purple-300 mt-1">{guestPhoto.name}</p>}
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={handleAddGuest}
                    disabled={addingGuest || !guestForm.name.trim() || !guestForm.parent_name.trim() || !guestForm.parent_contact.trim()}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="submit-guest-btn"
                  >
                    {addingGuest ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add to Session
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Guest entries are flagged for admin review so parents can be contacted to register formally.
                </p>
              </div>
            )}
          </Card>
        )}

        {/* Pre-filled from availability replies */}
        {!viewMode && prefilled > 0 && (
          <Card className="p-3 mb-4 bg-emerald-500/10 border-2 border-emerald-500/30"
            data-testid="rsvp-prefill-banner">
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-emerald-300">
                Pre-filled {prefilled} player{prefilled === 1 ? "" : "s"} from who said they were
                coming. Check it against the pitch before saving.
              </p>
              <Button
                size="sm"
                variant="outline"
                className="border-slate-600 text-slate-300 ml-auto"
                onClick={() => {
                  const blank = {};
                  players.forEach((p) => { blank[p.id] = ""; });
                  setAttendance(blank);
                  setPrefilled(0);
                }}
                data-testid="rsvp-clear-prefill-btn"
              >
                Clear and mark manually
              </Button>
            </div>
          </Card>
        )}

        {/* Bulk Actions - Only show if not in view mode */}
        {!viewMode && (isCoach || isAdmin) && players.length > 0 && (
          <Card className="p-4 mb-6 bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-white/5">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex gap-2">
                <Button
                  onClick={selectAll}
                  variant="outline"
                  size="sm"
                  className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                >
                  <CheckSquare className="w-4 h-4 mr-1" />
                  Select All
                </Button>
                <Button
                  onClick={deselectAll}
                  variant="outline"
                  size="sm"
                  className="border-slate-700 text-slate-300 hover:bg-[#0a0f1a]"
                >
                  <Square className="w-4 h-4 mr-1" />
                  Deselect All
                </Button>
              </div>
              
              <div className="h-6 w-px bg-gray-300"></div>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => markSelectedAs("present")}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={selectedPlayers.length === 0}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Mark Present
                </Button>
                <Button
                  onClick={() => markSelectedAs("absent")}
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={selectedPlayers.length === 0}
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  Mark Absent
                </Button>
                <Button
                  onClick={() => markSelectedAs("mc")}
                  size="sm"
                  className="bg-yellow-600 hover:bg-yellow-700 text-white"
                  disabled={selectedPlayers.length === 0}
                >
                  <FileText className="w-4 h-4 mr-1" />
                  Mark MC
                </Button>
              </div>
              
              {selectedPlayers.length > 0 && (
                <span className="ml-auto text-sm font-semibold text-blue-400">
                  {selectedPlayers.length} selected
                </span>
              )}
            </div>
          </Card>
        )}

        {/* Attendance List */}
        <Card className="overflow-hidden">
          {players.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-slate-400">No players signed up for this session</p>
            </div>
          ) : visiblePlayers.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-slate-400">Nobody has signed up for this term yet.</p>
              <button onClick={() => setShowNotSignedUp(true)}
                className="mt-2 text-sm text-yellow-400 underline"
                data-testid="show-not-signed-up-empty">
                Show the {notSignedUpList.length} children in this age group
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-blue-600 text-white">
                  <tr>
                    {!viewMode && (isCoach || isAdmin) && (
                      <th className="px-4 py-3 text-left w-12">
                        <span className="sr-only">Select</span>
                      </th>
                    )}
                    <th className="px-4 py-3 text-left">Player Name</th>
                    <th className="px-4 py-3 text-left">Membership #</th>
                    {showPaymentStatus && (
                      <th className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          Payment
                        </div>
                      </th>
                    )}
                    <th className="px-4 py-3 text-center">Attendance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {visiblePlayers.map((player, index) => {
                    const pId = player.id;
                    const status = attendance[pId];
                    const isSelected = selectedPlayers.includes(pId);
                    const paymentInfo = paymentStatuses[pId];
                    const firstNotSignedUp = showNotSignedUp
                      && notSignedUpList.length > 0
                      && player.id === notSignedUpList[0].id;

                    return (
                      <Fragment key={pId}>
                      {firstNotSignedUp && (
                        <tr key="not-signed-up-divider" className="bg-slate-800/80">
                          <td colSpan={6} className="px-4 py-2 text-xs text-amber-300"
                            data-testid="not-signed-up-divider">
                            Not signed up for this term — in the age group, no registration yet
                          </td>
                        </tr>
                      )}
                      <tr 
                        key={pId} 
                        className={`hover:bg-blue-500/10 transition-colors ${
                          isSelected ? 'bg-blue-500/20' : index % 2 === 0 ? 'bg-slate-900/50' : 'bg-[#0a0f1a]'
                        }`}
                      >
                        {!viewMode && (isCoach || isAdmin) && (
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => togglePlayerSelection(pId)}
                              className="w-5 h-5 text-blue-400 rounded border-slate-700 focus:ring-blue-500"
                            />
                          </td>
                        )}
                        <td className="px-4 py-4">
                          <div className="font-semibold text-white flex items-center gap-2">
                            {player.child_name}
                            {player.is_guest && (
                              <>
                                <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded">
                                  Guest
                                </span>
                                {(isCoach || isAdmin) && (
                                  <button
                                    onClick={() => {
                                      setLinkingGuest(player);
                                      setShowDropInSearch(true);
                                      setDropInQuery(player.child_name || "");
                                      window.scrollTo({ top: 0, behavior: "smooth" });
                                    }}
                                    className="text-[10px] text-blue-400 underline hover:text-blue-300"
                                    data-testid={`link-guest-${pId}`}
                                  >
                                    link to an existing child
                                  </button>
                                )}
                              </>
                            )}
                            {paymentInfo?.warning && (
                              <AlertTriangle className="w-4 h-4 text-amber-400" title="Payment overdue" />
                            )}
                            {paymentInfo?.days_per_week && (
                              <span className="text-[10px] bg-slate-700 text-slate-200 px-2 py-0.5 rounded"
                                title={(paymentInfo.days || []).join(", ")}
                                data-testid={`package-${pId}`}>
                                {paymentInfo.days_per_week}x/week
                              </span>
                            )}
                            {paymentInfo?.social && (
                              <span className="text-[10px] bg-sky-500/20 text-sky-300 px-2 py-0.5 rounded"
                                data-testid={`social-${pId}`}>
                                Social
                              </span>
                            )}
                          </div>
                          {/* Which terms this child has signed up for, and
                              whether the term being marked is missing or owed.
                              A holding register lists everyone, so this is how
                              the coach tells who is current. */}
                          {(isCoach || isAdmin) && !player.is_guest && paymentInfo && (
                            <div className="flex flex-wrap items-center gap-1 mt-1"
                              data-testid={`term-badges-${pId}`}>
                              {(paymentInfo.terms || []).map((t) => (
                                <span key={t.label}
                                  title={`${t.term_name || t.label}${t.paid === false && t.outstanding ? ` · $${t.outstanding} owing` : ""}${t.inferred ? " · placed by the date they registered" : ""}`}
                                  className={`text-[10px] px-1.5 py-0.5 rounded ${
                                    t.paid === false
                                      ? "bg-amber-500/20 text-amber-300"
                                      : "bg-emerald-500/15 text-emerald-300"
                                  }`}>
                                  {t.label}
                                  {t.paid === false && t.outstanding ? ` $${t.outstanding}` : ""}
                                </span>
                              ))}
                              {paymentInfo.registeredThisTerm === false && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300"
                                  data-testid={`term-missing-${pId}`}>
                                  {session?.term_name ? `${_shortTerm(session.term_name)} missing` : "not signed up"}
                                </span>
                              )}
                              {(paymentInfo.terms || []).length === 0
                                && paymentInfo.registeredThisTerm !== false && (
                                <span className="text-[10px] text-slate-500">no term history</span>
                              )}
                            </div>
                          )}
                          {typeof rsvps[pId]?.attending === "boolean" && (
                            <span
                              className={`inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full ${
                                rsvps[pId].attending
                                  ? "bg-emerald-500/20 text-emerald-300"
                                  : "bg-amber-500/20 text-amber-300"
                              }`}
                              data-testid={`rsvp-badge-${pId}`}
                            >
                              {rsvps[pId].attending ? "said coming" : "said not coming"}
                              {rsvps[pId].source === "whatsapp" ? " · WhatsApp" : ""}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-slate-400">
                          {player.membership_number || player.email || '-'}
                        </td>
                        {showPaymentStatus && (
                          <td className="px-4 py-4">
                            <div className="flex justify-center">
                              {paymentInfo && paymentInfo.status !== 'no_enrollment' ? (
                                paymentInfo.paid ? (
                                  <Badge className="text-xs bg-emerald-500/20 text-emerald-400 border-emerald-500/30" data-testid={`att-paid-${pId}`}>
                                    <DollarSign className="w-3 h-3 mr-0.5" />Paid
                                  </Badge>
                                ) : (
                                  <Badge className="text-xs bg-red-500/20 text-red-400 border-red-500/30" data-testid={`att-unpaid-${pId}`}>
                                    Unpaid{paymentInfo.outstanding ? ` $${paymentInfo.outstanding}` : ''}
                                  </Badge>
                                )
                              ) : (
                                <span className="text-slate-500 text-sm">-</span>
                              )}
                            </div>
                          </td>
                        )}
                        <td className="px-4 py-4">
                          {viewMode ? (
                            <div className="flex justify-center">
                              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                                status === 'present'
                                  ? 'bg-emerald-500/20 text-emerald-400'
                                  : status === 'mc'
                                  ? 'bg-yellow-500/20 text-yellow-400'
                                  : status === 'absent'
                                  ? 'bg-red-500/20 text-red-400'
                                  : 'bg-slate-700/50 text-slate-400'
                              }`}>
                                {status === 'present' ? '✓ Present' :
                                 status === 'mc' ? '🏥 MC' :
                                 status === 'absent' ? '✗ Absent' : '— not marked'}
                              </span>
                              {markedBy[pId] && (
                                <p className="text-[11px] text-slate-500 mt-1 text-center"
                                  data-testid={`marked-by-${pId}`}>
                                  by {markedBy[pId].by}
                                  {markedBy[pId].at
                                    ? ` · ${new Date(markedBy[pId].at).toLocaleString("en-SG", {
                                        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`
                                    : ""}
                                  {markedBy[pId].confirmed ? " · confirmed" : ""}
                                </p>
                              )}
                            </div>
                          ) : (
                            <div className="flex justify-center">
                              <select
                                value={status || ""}
                                onChange={(e) => handleAttendanceChange(pId, e.target.value)}
                                className={`px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                  !status 
                                    ? "border-red-400 border-2 bg-red-500/10" 
                                    : "border-slate-700"
                                }`}
                                data-testid={`attendance-select-${pId}`}
                              >
                                <option value="" disabled>⚠️ Select...</option>
                                <option value="present">✓ Present</option>
                                <option value="absent">✗ Absent</option>
                                <option value="mc">🏥 Medical Certificate</option>
                              </select>
                            </div>
                          )}
                        </td>
                      </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
              {notSignedUpList.length > 0 && (
                <div className="p-3 border-t border-white/5">
                  <button
                    onClick={() => setShowNotSignedUp((v) => !v)}
                    className="text-xs text-slate-300 hover:text-white underline"
                    data-testid="toggle-not-signed-up"
                  >
                    {showNotSignedUp
                      ? "Hide children who haven't signed up for this term"
                      : `Show ${notSignedUpList.length} child(ren) in this age group who haven't signed up`}
                  </button>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Phone: one tap for the whole squad. The tick column is hard to use
            on a 400px screen, so the actions follow the coach down the list. */}
        {!viewMode && (isCoach || isAdmin) && players.length > 0 && (
          <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-slate-900/95
                          border-t border-white/10 px-3 py-2 backdrop-blur"
            data-testid="attendance-sticky-bar">
            <div className="flex items-center gap-2">
              <button onClick={selectedPlayers.length ? deselectAll : selectAll}
                className="text-xs text-slate-300 underline shrink-0"
                data-testid="sticky-select-all">
                {selectedPlayers.length ? "Clear" : "Select all"}
              </button>
              <Button onClick={() => markSelectedAs("present")}
                disabled={!selectedPlayers.length}
                className="flex-1 h-11 bg-green-600 hover:bg-green-700 text-white"
                data-testid="sticky-mark-present">
                Present ({selectedPlayers.length})
              </Button>
              <Button onClick={() => markSelectedAs("absent")}
                disabled={!selectedPlayers.length}
                className="h-11 bg-red-600 hover:bg-red-700 text-white px-3"
                data-testid="sticky-mark-absent">
                Absent
              </Button>
              <Button onClick={saveAttendance} disabled={saving}
                className="h-11 bg-blue-600 hover:bg-blue-700 text-white px-3"
                data-testid="sticky-save">
                Save
              </Button>
            </div>
          </div>
        )}

        {/* Save Button */}
        {!viewMode && (isCoach || isAdmin) && players.length > 0 && (
          <div className="mt-6 flex justify-end">
            <Button
              onClick={saveAttendance}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-semibold"
            >
              {saving ? (
                <>
                  <div className="spinner mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save Attendance
                </>
              )}
            </Button>
          </div>
        )}

        {viewMode && (
          <div className="mt-6 space-y-3">
            <Card className="p-4 bg-emerald-500/10 border-2 border-emerald-500/30">
              <p className="text-center text-emerald-400 font-semibold">
                ✓ Attendance has been recorded for this session
                {confirmed ? " and confirmed" : ""}
              </p>
            </Card>
            <div className="flex flex-wrap justify-center gap-3">
              {(isCoach || isAdmin) && (
                <Button variant="outline" onClick={() => setViewMode(false)}
                  data-testid="edit-attendance-btn">
                  Edit these marks
                </Button>
              )}
              {isAdmin && !confirmed && (
                <Button onClick={confirmAttendance} disabled={confirming}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="confirm-attendance-btn">
                  {confirming ? "Confirming..." : "Confirm this register"}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
